import type { ContextUser } from "../../shared/api/client";

export type AutocompleteUser = Pick<ContextUser, "email" | "id" | "name">;

type RankedUser<TUser extends AutocompleteUser> = {
  index: number;
  rank: number;
  user: TUser;
};

export function filterUsersForAutocomplete<TUser extends AutocompleteUser>(
  users: readonly TUser[],
  query: string,
): TUser[] {
  const normalizedQuery = normalizeAutocompleteText(query);
  const ranked = users
    .map((user, index) => toRankedUser(user, index, normalizedQuery))
    .filter((item): item is RankedUser<TUser> => item !== null)
    .sort((left, right) => left.rank - right.rank || left.index - right.index);

  return ranked.map((item) => item.user);
}

export type ActiveMentionFragment = {
  start: number;
  end: number;
  query: string;
};

export function getUserMentionToken(user: AutocompleteUser): string {
  const localPart = user.email.split("@")[0]?.trim();
  if (localPart !== undefined && localPart.length > 0) {
    return `@${localPart.toLowerCase()}`;
  }

  return `@${normalizeAutocompleteText(user.name).replace(/\s+/g, ".")}`;
}

export function getUserDisplayLabel(user: AutocompleteUser): string {
  return `${user.name} · ${user.email}`;
}

export function getActiveMentionFragment(
  value: string,
  cursor: number,
): ActiveMentionFragment | null {
  const beforeCursor = value.slice(0, cursor);
  const match = /(^|\s)@([^\s@]*)$/.exec(beforeCursor);
  if (match === null) {
    return null;
  }

  const prefix = match[1] ?? "";
  const query = match[2] ?? "";
  const start = cursor - query.length - 1;
  if (prefix.length === 0 || /\s/.test(prefix)) {
    return { start, end: cursor, query };
  }

  return null;
}

export function replaceActiveMentionFragment(
  value: string,
  cursor: number,
  mentionToken: string,
): { value: string; cursor: number } {
  const fragment = getActiveMentionFragment(value, cursor);
  if (fragment === null) {
    return { value, cursor };
  }

  const nextCharacter = value[fragment.end] ?? "";
  const needsInsertedSpace = nextCharacter.length === 0 || !/\s/.test(nextCharacter);
  const replacement = needsInsertedSpace ? `${mentionToken} ` : mentionToken;
  const nextValue = `${value.slice(0, fragment.start)}${replacement}${value.slice(fragment.end)}`;
  const nextCursor = fragment.start + mentionToken.length + 1;

  return { value: nextValue, cursor: nextCursor };
}

function toRankedUser<TUser extends AutocompleteUser>(
  user: TUser,
  index: number,
  query: string,
): RankedUser<TUser> | null {
  const rank = getUserMatchRank(user, query);
  return rank === null ? null : { index, rank, user };
}

function getUserMatchRank(user: AutocompleteUser, query: string): number | null {
  if (query.length === 0) {
    return 50;
  }

  const name = normalizeAutocompleteText(user.name);
  const email = normalizeAutocompleteText(user.email);
  const emailLocalPart = email.split("@")[0] ?? "";
  const nameTokens = name.split(/\s+/).filter(Boolean);

  if (name.startsWith(query)) return 0;
  if (emailLocalPart.startsWith(query)) return 1;
  if (email.startsWith(query)) return 2;
  if (nameTokens.some((token) => token.startsWith(query))) return 3;
  if (name.includes(query)) return 4;
  if (email.includes(query)) return 5;

  return null;
}

function normalizeAutocompleteText(value: string): string {
  return value.trim().toLocaleLowerCase();
}
